import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import GTranslateOutlinedIcon from '@mui/icons-material/GTranslateOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { ListItemButton, ListItemIcon } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Link } from 'gatsby';
import * as React from 'react';
import { cameraIcon, logo } from '../assets/icons';
import { AuthUserContext } from '../contexts/AuthUserContext';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { NovadeLiteUrlContext } from '../contexts/NovadeLiteUrlContext';
import { useStrapiHeader } from '../hooks/useStrapiHeader';
import { HeaderContainer, StyledAvatar } from '../styles/Header';
import { AuthUserContextType, LocationLanguageContextType, LogInResponse, NovadeLiteUrlContextType } from '../typings';
import { enterToSearch, getInitials } from '../utils/common';
import { getAllSupportedLanguages, Languages } from '../utils/getAllSupportedLanguages';
import { setGTMEvent } from '../utils/setGAEvent';
import LoginDialog from './LoginDialog';

interface HeaderMobileProps {
  isComingSoonPage: boolean;
}

const HeaderMobile: React.FC<HeaderMobileProps> = ({ isComingSoonPage }) => {
  const [searchFieldValue, setSearchFieldValue] = React.useState('');
  const [isOpenDrawer, setIsOpenDrawer] = React.useState(false);
  const [isOpenDialog, setIsOpenDialog] = React.useState(false);
  const [isSearchbarOpen, setIsSearchbarOpen] = React.useState(false);

  const authUser = React.useContext<AuthUserContextType>(AuthUserContext);
  const novadeLiteAPI = React.useContext<NovadeLiteUrlContextType>(NovadeLiteUrlContext);
  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const language: string = locationLanguage?.language ?? Languages.English;
  const databasesMap = novadeLiteAPI ? authUser?.databasesMap[novadeLiteAPI.novadeLiteUrl] || {} : {};
  const { token } = Object.values(databasesMap)[0] ?? '';

  const headerContent = useStrapiHeader(language);
  const handleLanguageChange = (event: SelectChangeEvent) => {
    locationLanguage?.updateLanguage(event.target.value as string);
    setIsOpenDrawer(false);
  };

  const login = () => {
    setIsOpenDialog(true);
  };
  async function submitLogoutRequest() {
    const NOVADE_LITE_LOGOUT_URL = `${novadeLiteAPI?.novadeLiteUrl}/logout`;
    const response = await fetch(NOVADE_LITE_LOGOUT_URL, {
      method: 'POST',
      headers: token ? { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
      body: ''
    });
    if (!response.ok) {
      console.log('Error logging out');
    } else {
      setGTMEvent('logout', 'logout', 'Logout from Novade Template App');
      novadeLiteAPI?.updateNovadeLiteUrl(process.env.GATSBY_NOVADE_LITE_LOGIN_URL_SEA!);
    }
  }
  const logout = () => {
    authUser?.logout();
    novadeLiteAPI?.updateNovadeLiteUrl(process.env.GATSBY_NOVADE_LITE_LOGIN_URL_SEA!);
    submitLogoutRequest();
    localStorage.clear();
    setIsOpenDialog(false);
    setIsOpenDrawer(false);
  };

  const search = (event: React.KeyboardEvent) => {
    enterToSearch(event, searchFieldValue);
  };

  const toggleDrawer = () => {
    setIsOpenDrawer((isOpen) => !isOpen);
  };
  const toggleSearchbar = () => {
    setIsSearchbarOpen((isOpen) => !isOpen);
  };
  const handleDialogClose = (value: LogInResponse | undefined) => {
    if (value) {
      authUser?.updateUser({
        email: value.email,
        displayName: value.userDisplayName
      });
      authUser?.updateDatabasesMap(value.databases);
    }
    setIsOpenDialog(false);
    setIsOpenDrawer(false);
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFieldValue(event.target.value);
  };
  const clearSearchbar = () => {
    setSearchFieldValue('');
  };

  const menu = [
    { icon: <HomeOutlinedIcon />, label: headerContent?.home, link: '/' },
    { icon: <PageviewOutlinedIcon />, label: headerContent?.templates, link: '/browse' },
    { icon: <DashboardOutlined />, label: headerContent?.dashboards, link: '/dashboards' }
  ];

  return (
    <AppBar position='sticky' color='transparent'>
      {headerContent ? (
        <>
          {isSearchbarOpen ? (
            <Toolbar style={{ backgroundColor: 'white' }}>
              <HeaderContainer>
                <IconButton aria-label='search' onClick={toggleSearchbar}>
                  <ArrowBackIcon />
                </IconButton>
                <InputBase
                  onChange={handleSearchChange}
                  fullWidth
                  onKeyDown={search}
                  value={searchFieldValue}
                  placeholder={headerContent.search}
                  inputProps={{ 'aria-label': 'Search for templates' }}
                />
                {searchFieldValue.length > 0 ? (
                  <IconButton aria-label='clear' onClick={clearSearchbar}>
                    <ClearIcon />
                  </IconButton>
                ) : null}
              </HeaderContainer>
            </Toolbar>
          ) : (
            <Toolbar style={{ backgroundColor: 'white' }}>
              <HeaderContainer>
                {!isComingSoonPage ? (
                  <IconButton aria-label='menu' onClick={toggleDrawer}>
                    <MenuIcon />
                  </IconButton>
                ) : null}
                <div />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <img src={logo} alt='Novade Logo' width={30} />
                </div>
                <div />
                {!isComingSoonPage ? (
                  <IconButton aria-label='search' onClick={toggleSearchbar}>
                    <SearchIcon />
                  </IconButton>
                ) : null}
              </HeaderContainer>
              <Drawer anchor='left' open={isOpenDrawer} onClose={toggleDrawer}>
                <List component='nav' aria-label='mobile menu' style={{ minWidth: '260px' }}>
                  {authUser?.user ? (
                    <div>
                      <ListItem style={{ marginBottom: '0.5rem' }}>
                        {authUser.user.displayName ? (
                          <StyledAvatar>{getInitials(authUser.user.displayName)}</StyledAvatar>
                        ) : (
                          <img src={cameraIcon} alt='User' width={48} />
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '1rem' }}>
                          <Typography variant='body2'>{authUser.user.displayName}</Typography>
                          <Typography variant='body2'>{authUser.user.email}</Typography>
                        </div>
                      </ListItem>
                      <Divider />
                    </div>
                  ) : null}
                  {menu.map((item) => (
                    <ListItemButton key={item.label} onClick={toggleDrawer} component={Link} to={item.link}>
                      <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  ))}
                  <ListItem>
                    <Select
                      fullWidth
                      disableUnderline
                      value={language}
                      onChange={handleLanguageChange}
                      startAdornment={
                        <InputAdornment position='start'>
                          <GTranslateOutlinedIcon />
                        </InputAdornment>
                      }
                    >
                      {getAllSupportedLanguages().map((eachLang) => (
                        <MenuItem key={eachLang.locale} value={eachLang.locale}>
                          {eachLang.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </ListItem>
                </List>
                <List component='nav' aria-label='mobile menu' style={{ minWidth: '260px', position: 'absolute', bottom: '0' }}>
                  {authUser?.user ? (
                    <ListItemButton onClick={logout}>
                      <ExitToAppOutlinedIcon />
                      &nbsp;&nbsp;
                      <ListItemText primary={headerContent.logout} />
                    </ListItemButton>
                  ) : (
                    <ListItemButton onClick={login}>
                      <ExitToAppOutlinedIcon />
                      &nbsp;&nbsp;
                      <ListItemText primary={headerContent.login} />
                    </ListItemButton>
                  )}
                  <LoginDialog open={isOpenDialog} onDialogClose={handleDialogClose} />
                </List>
              </Drawer>
            </Toolbar>
          )}
        </>
      ) : null}
    </AppBar>
  );
};

export default HeaderMobile;
